/**
 * «Lo último de Jimmy» — GEN-EKB-029 (primera entrega: encargos de aprendizaje), GEN-PX-033 (los 3 más recientes). Bajo «Pendiente de ti»
 * en Saber, solo si hay alguno: los encargos en curso (⏳ título, quién lo pidió, «n iKus de ~m», KB de destino, barra) y los terminados
 * (✓ «n iKus → KB», dudas, «Ver» abre el KB), sin límite de semana. El kernel entrega solo los 3 más recientes (GET /v1/ekb/encargos) y
 * el total; «Historial» pide todos. Sondea mientras haya alguno en curso. Se pinta al momento con lo precargado y se revalida.
 * Mockup: Saber-2-nube.html, bloque «Jimmy, esta semana» (.enc .row, .st, .q small, .bar).
 */
import { useEffect, useState } from 'react'
import { listEncargos } from '../api/client.js'
import { useSession } from '../context/SessionContext.jsx'
import { claveDe, encargosGuardados, guardarEncargos } from '../lib/saberCache.js'

export const SONDEO_MS = 5000
const dudasMax = 3

const fecha = iso => {
  if (!iso) return ''
  const d = new Date(iso)
  const hoy = new Date()
  const ayer = new Date(hoy.getTime() - 86400000)
  if (d.toDateString() === hoy.toDateString()) return 'hoy'
  if (d.toDateString() === ayer.toDateString()) return 'ayer'
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
}
const iku = n => `${n} ${n === 1 ? 'iKu' : 'iKus'}`

function Fila({ e, yo, onVer }) {
  const quien = e.solicitante?.email && e.solicitante.email === yo ? 'Encargo tuyo' : `Encargo de ${e.solicitante?.display_name ?? e.solicitante?.email ?? 'alguien'}`
  const destino = e.kb ? (e.kbs_total > 1 ? `${e.kb.nombre} y ${e.kbs_total - 1} más` : e.kb.nombre) : null
  if (e.estado === 'terminado' && !e.ikus) {
    // Sin iKus no hay «Aprendido»: con fallos es un «!»; si todo se saltó (p. ej. ya aprendido) es un aviso sin ✓ de éxito.
    const fallo = e.fallidos > 0
    const motivos = fallo ? e.fallos ?? [] : e.saltos ?? []
    return (
      <div className="dk-enc__fila">
        <span className="dk-enc__st" aria-hidden="true">{fallo ? '!' : '–'}</span>
        <div className="dk-enc__q">
          {fallo ? 'No aprendido' : motivos.length ? 'Ya aprendido' : 'Sin iKus nuevos en'} «{e.titulo}»
          <small>{quien} · {fecha(e.fin)}{fallo ? ` · ${e.fallidos} con fallos` : ''}</small>
          {motivos.filter(Boolean).map((m, i) => <small key={i}>{fallo ? 'Fallo' : 'Motivo'}: {m}</small>)}
        </div>
      </div>
    )
  }
  if (e.estado === 'terminado') {
    return (
      <div className="dk-enc__fila">
        <span className="dk-enc__st" aria-hidden="true">✓</span>
        <div className="dk-enc__q">
          Aprendido «{e.titulo}»
          <small>{quien} · {iku(e.ikus)}{destino ? ` → ${destino}` : ''} · {fecha(e.fin)}{e.fallidos ? ` · ${e.fallidos} con fallos` : ''}</small>
          {(e.dudas ?? []).slice(0, dudasMax).map((d, i) => <small key={i}>Duda: {d}</small>)}
          {(e.dudas ?? []).length > dudasMax && <small>y {e.dudas.length - dudasMax} dudas más</small>}
        </div>
        {e.kb?.id && <button type="button" className="dk-btn" onClick={() => onVer(e.kb)}>Ver</button>}
      </div>
    )
  }
  const parado = e.estado === 'interrumpido'
  const pct = Math.round((e.avance ?? 0) * 100)
  return (
    <div className="dk-enc__fila">
      <span className={`dk-enc__st${parado ? '' : ' dk-enc__st--run'}`} aria-hidden="true">{parado ? '!' : '⏳'}</span>
      <div className="dk-enc__q">
        {parado ? 'Interrumpido' : 'Aprendiendo'} «{e.titulo}»
        <small>
          {quien} · {e.ikus_estimados ? `${e.ikus} iKus redactados de ~${e.ikus_estimados}` : `${iku(e.ikus)} redactados`}{destino ? ` · → ${destino}` : ''}
          {parado ? ' · sin avance desde hace rato' : ''}
        </small>
        {!parado && <div className="dk-enc__bar" role="progressbar" aria-label="Avance" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}><i style={{ width: `${pct}%` }} /></div>}
      </div>
    </div>
  )
}

export default function SaberEncargos({ refresco = 0, onVer }) {
  const { user } = useSession()
  const clave = claveDe(user)
  const [r, setR] = useState(() => encargosGuardados(clave)) // GEN-PX-033: lo precargado al entrar
  const [todas, setTodas] = useState(false)
  const filas = r?.encargos ?? []
  const hayEnCurso = filas.some(e => e.estado === 'en_curso')

  // Una carga por cada respuesta de Jimmy (refresco), al abrir/cerrar «Historial» y, mientras haya un encargo en curso, una cada SONDEO_MS.
  const cargar = (vivo, todos) => Promise.resolve().then(() => listEncargos({ todos })).then(x => {
    if (!vivo()) return
    if (!todos) guardarEncargos(clave, x)
    setR(x)
  }).catch(() => {})
  useEffect(() => {
    let vivo = true
    cargar(() => vivo, todas)
    return () => { vivo = false }
  }, [refresco, todas]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hayEnCurso) return undefined
    let vivo = true
    const t = setInterval(() => cargar(() => vivo, todas), SONDEO_MS)
    return () => { vivo = false; clearInterval(t) }
  }, [hayEnCurso, todas]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!filas.length) return null
  const hayMas = (r?.total ?? filas.length) > 3
  return (
    <section className="dk-enc" aria-label="Lo último de Jimmy">
      <div className="dk-enc__cab">
        <h2>Lo último de Jimmy</h2><span className="dk-enc__sub">encargos de aprendizaje</span>
        {hayMas && <button type="button" className="dk-enc__hist" onClick={() => setTodas(v => !v)}>{todas ? 'Menos' : 'Historial'}</button>}
      </div>
      <div className="dk-enc__lista">
        {filas.map(e => <Fila key={e.id} e={e} yo={user?.email} onVer={onVer} />)}
      </div>
    </section>
  )
}
